import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Brain,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Radio,
  TrendingUp,
  Shield,
  Info,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Risk level configurations
const RISK_CONFIG = {
  low: {
    color: "#34C759",
    bgColor: "rgba(52, 199, 89, 0.1)",
    borderColor: "rgba(52, 199, 89, 0.3)",
    icon: CheckCircle2,
    label: "Bajo Riesgo",
  },
  medium: {
    color: "#FFCC00",
    bgColor: "rgba(255, 204, 0, 0.1)",
    borderColor: "rgba(255, 204, 0, 0.3)",
    icon: AlertTriangle,
    label: "Riesgo Medio",
  },
  high: {
    color: "#FF9500",
    bgColor: "rgba(255, 149, 0, 0.1)",
    borderColor: "rgba(255, 149, 0, 0.3)",
    icon: AlertTriangle,
    label: "Riesgo Alto",
  },
  critical: {
    color: "#FF3B30",
    bgColor: "rgba(255, 59, 48, 0.1)",
    borderColor: "rgba(255, 59, 48, 0.3)",
    icon: XCircle,
    label: "Crítico",
  },
};

// Status configurations for overall analysis
const STATUS_CONFIG = {
  good: {
    color: "#34C759",
    bgColor: "rgba(52, 199, 89, 0.15)",
    icon: "✅",
    label: "Óptimo",
  },
  caution: {
    color: "#FFCC00",
    bgColor: "rgba(255, 204, 0, 0.15)",
    icon: "🟡",
    label: "Precaución",
  },
  warning: {
    color: "#FF9500",
    bgColor: "rgba(255, 149, 0, 0.15)",
    icon: "⚠️",
    label: "Alerta",
  },
  critical: {
    color: "#FF3B30",
    bgColor: "rgba(255, 59, 48, 0.15)",
    icon: "⛔",
    label: "Crítico",
  },
};

// Quick analysis widget for frequencies page
export function RFPredictionWidget({ frequencies, equipmentName, onAnalysisComplete }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const runQuickAnalysis = async () => {
    if (!frequencies || frequencies.length === 0) {
      toast.error("No hay frecuencias para analizar");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE}/api/rf-ai/quick-analysis`,
        {
          frequencies: frequencies,
          equipment_name: equipmentName || "Sistema IEM",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAnalysis(response.data.data);
        if (onAnalysisComplete) {
          onAnalysisComplete(response.data.data);
        }
      }
    } catch (error) {
      toast.error("Error al analizar frecuencias");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = analysis
    ? STATUS_CONFIG[analysis.summary?.overall_status] || STATUS_CONFIG.caution
    : null;

  return (
    <div className="space-y-3">
      {/* Analysis Button */}
      <Button
        onClick={runQuickAnalysis}
        disabled={loading || !frequencies?.length}
        variant="outline"
        className="w-full border-[#8B5CF6]/50 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]"
        data-testid="quick-analysis-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Analizando...
          </>
        ) : (
          <>
            <Brain className="w-4 h-4 mr-2" />
            Analizar Riesgos RF
          </>
        )}
      </Button>

      {/* Quick Results */}
      {analysis && (
        <div
          className="p-3 rounded-lg border"
          style={{
            backgroundColor: statusConfig.bgColor,
            borderColor: statusConfig.color,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">{statusConfig.icon}</span>
              <span
                className="font-semibold text-sm"
                style={{ color: statusConfig.color }}
              >
                {statusConfig.label}
              </span>
            </div>
            <span
              className="text-xs font-mono px-2 py-1 rounded"
              style={{ backgroundColor: `${statusConfig.color}20` }}
            >
              {analysis.summary?.average_risk}% riesgo
            </span>
          </div>

          <p className="text-xs text-[#A1A1AA] mb-2">
            {analysis.summary?.overall_message}
          </p>

          {/* Problem frequencies */}
          {analysis.frequencies?.filter((f) => f.risk_level === "critical" || f.risk_level === "high").length > 0 && (
            <div className="space-y-1 mt-2 pt-2 border-t border-white/10">
              <p className="text-[10px] text-[#71717A] uppercase tracking-wider">
                Frecuencias problemáticas:
              </p>
              {analysis.frequencies
                .filter((f) => f.risk_level === "critical" || f.risk_level === "high")
                .map((freq, idx) => {
                  const config = RISK_CONFIG[freq.risk_level];
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-xs py-1"
                    >
                      <span className="font-mono" style={{ color: config.color }}>
                        {freq.frequency.toFixed(3)} MHz
                      </span>
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px]"
                        style={{
                          backgroundColor: config.bgColor,
                          color: config.color,
                        }}
                      >
                        {config.label}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Detailed AI Analysis Dialog
export function AIAnalysisDialog({
  open,
  onOpenChange,
  frequencies,
  equipmentName,
  venueName,
  eventType,
}) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const runDetailedAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE}/api/rf-ai/detailed-analysis`,
        {
          frequencies: frequencies,
          equipment_name: equipmentName || "Sistema IEM",
          venue_name: venueName,
          event_type: eventType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data.success) {
        setAnalysis(response.data.data);
      } else {
        setError(response.data.data?.error || "Error en el análisis");
        // Use fallback local analysis if available
        if (response.data.data?.fallback) {
          setAnalysis({ local_analysis: response.data.data.fallback });
        }
      }
    } catch (err) {
      setError("Error de conexión con el servidor");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Run analysis when dialog opens
  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (isOpen && !analysis && !loading) {
      runDetailedAnalysis();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#111111] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
            Análisis IA de Frecuencias RF
          </DialogTitle>
          <DialogDescription className="text-[#71717A]">
            Análisis detallado con recomendaciones inteligentes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <Brain className="w-12 h-12 text-[#8B5CF6] animate-pulse" />
                <Sparkles className="w-4 h-4 text-[#8B5CF6] absolute -top-1 -right-1 animate-bounce" />
              </div>
              <p className="text-[#A1A1AA] mt-4">Analizando con IA...</p>
              <p className="text-xs text-[#71717A] mt-1">
                Esto puede tomar unos segundos
              </p>
            </div>
          ) : error ? (
            <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                <span className="font-medium text-[#FF3B30]">Error en análisis IA</span>
              </div>
              <p className="text-sm text-[#A1A1AA]">{error}</p>
              {analysis?.local_analysis && (
                <p className="text-xs text-[#71717A] mt-2">
                  Mostrando análisis local como alternativa.
                </p>
              )}
            </div>
          ) : null}

          {/* AI Analysis Results */}
          {analysis?.ai_analysis && (
            <div className="space-y-4">
              <div className="p-4 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-[#8B5CF6]" />
                  <span className="font-semibold text-[#8B5CF6]">
                    Análisis IA
                  </span>
                </div>
                <div className="prose prose-sm prose-invert max-w-none">
                  <div
                    className="text-sm text-[#E4E4E7] whitespace-pre-wrap leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: analysis.ai_analysis
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                        .replace(/\n/g, "<br />"),
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Local Analysis Summary */}
          {analysis?.local_analysis && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-[#71717A]">
                <Zap className="w-4 h-4" />
                <span>Análisis Local de Riesgos</span>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-2">
                <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
                  <p className="text-lg font-bold text-[#F4F4F5]">
                    {analysis.local_analysis.summary?.total_frequencies || 0}
                  </p>
                  <p className="text-[10px] text-[#71717A]">Frecuencias</p>
                </div>
                <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
                  <p className="text-lg font-bold text-[#34C759]">
                    {Math.round(100 - (analysis.local_analysis.summary?.average_risk || 0))}%
                  </p>
                  <p className="text-[10px] text-[#71717A]">Seguridad</p>
                </div>
                <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
                  <p className="text-lg font-bold text-[#FF3B30]">
                    {analysis.local_analysis.summary?.critical_count || 0}
                  </p>
                  <p className="text-[10px] text-[#71717A]">Críticas</p>
                </div>
                <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
                  <p className="text-lg font-bold text-[#FF9500]">
                    {analysis.local_analysis.summary?.high_count || 0}
                  </p>
                  <p className="text-[10px] text-[#71717A]">Alto Riesgo</p>
                </div>
              </div>

              {/* Frequency Details */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {analysis.local_analysis.frequencies?.map((freq, idx) => {
                  const config = RISK_CONFIG[freq.risk_level];
                  const IconComponent = config.icon;
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-[#0A0A0A] rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <IconComponent
                          className="w-4 h-4"
                          style={{ color: config.color }}
                        />
                        <span className="font-mono text-sm text-white">
                          {freq.frequency.toFixed(3)} MHz
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${freq.risk_score}%`,
                              backgroundColor: config.color,
                            }}
                          />
                        </div>
                        <span
                          className="text-xs font-mono w-10 text-right"
                          style={{ color: config.color }}
                        >
                          {freq.risk_score}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t border-white/10">
          <p className="text-xs text-[#71717A]">
            {analysis?.generated_at
              ? `Generado: ${new Date(analysis.generated_at).toLocaleString()}`
              : ""}
          </p>
          <Button
            onClick={runDetailedAnalysis}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-[#8B5CF6]/50 text-[#8B5CF6]"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Brain className="w-4 h-4 mr-1" />
                Re-analizar
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Dashboard Prediction Widget
export function DashboardPredictionWidget() {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPrediction = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE}/api/rf-ai/dashboard-prediction`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setPrediction(response.data);
      }
    } catch (error) {
      console.error("Error fetching prediction:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useState(() => {
    fetchPrediction();
  }, []);

  if (loading) {
    return (
      <Card className="bg-[#111111] border-white/[0.08]">
        <CardContent className="p-4 flex items-center justify-center h-20">
          <Loader2 className="w-6 h-6 text-[#8B5CF6] animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!prediction?.has_systems) {
    return (
      <Card className="bg-[#111111] border-white/[0.08]">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#8B5CF6]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#A1A1AA]">Predicción RF</p>
            <p className="text-xs text-[#71717A]">
              Agrega sistemas para ver predicciones de interferencia
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const pred = prediction.prediction;
  const statusConfig = STATUS_CONFIG[pred?.status] || STATUS_CONFIG.caution;

  return (
    <Card
      className="border"
      style={{
        backgroundColor: "#111111",
        borderColor: `${statusConfig.color}40`,
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          {/* Left: Icon + Status */}
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${statusConfig.color}15` }}
            >
              <span className="text-3xl">{pred?.icon}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: statusConfig.color }} />
                <span className="text-sm text-[#71717A]">Predicción RF</span>
              </div>
              <p className="text-xl font-bold" style={{ color: statusConfig.color }}>
                {pred?.label}
              </p>
              <p className="text-xs text-[#71717A]">
                {pred?.total_systems} sistemas activos
              </p>
            </div>
          </div>

          {/* Center: Message */}
          <div className="hidden md:block flex-1 px-8">
            <p className="text-sm text-[#A1A1AA]">{pred?.message}</p>
          </div>

          {/* Right: Security Score */}
          <div className="text-right">
            <p
              className="text-4xl font-bold font-mono"
              style={{ color: statusConfig.color }}
            >
              {Math.round(100 - pred?.risk_score)}%
            </p>
            <p className="text-xs text-[#71717A]">seguridad</p>
          </div>
        </div>

        {/* Alerts row */}
        {(pred?.critical_count > 0 || pred?.high_risk_count > 0) && (
          <div className="mt-3 pt-3 border-t border-white/[0.08] flex items-center gap-4">
            <AlertTriangle className="w-4 h-4 text-[#FF9500]" />
            <p className="text-sm text-[#FF9500]">
              {pred?.critical_count > 0 && `${pred.critical_count} frecuencia(s) crítica(s)`}
              {pred?.critical_count > 0 && pred?.high_risk_count > 0 && " • "}
              {pred?.high_risk_count > 0 && `${pred.high_risk_count} con alto riesgo`}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default {
  RFPredictionWidget,
  AIAnalysisDialog,
  DashboardPredictionWidget,
};
